/**
 * Sofa silhouette SVG — vanilla TS port of the React `Sofa` component in
 * _design_ref_mobile_2026-05-24/sofa-shapes.jsx (the v49 shape-tile pictogram).
 *
 * Returns an <svg> element drawn from seat count + chaise flag, so the shape
 * carousel tiles render the real silhouette (not a text label). Used by the
 * MobileApp 01 · Shape row and the "see more" shape modal.
 */

const NS = "http://www.w3.org/2000/svg";

export interface SofaSVGOpts {
  seats: number;
  chaise?: boolean;
  /** Fill for body + cushions (use INK/PAPER for the silhouette row). */
  body?: string;
  cushion?: string;
  /** Leg fill. */
  legs?: string;
  /** Ground shadow color (transparent for the inline row). */
  shadow?: string;
  arm?: "soft" | "flat" | "pill";
  cushionStyle?: "plump" | "tight" | "sleek";
}

function rect(
  svg: SVGSVGElement,
  x: number,
  y: number,
  w: number,
  h: number,
  rx: number,
  fill: string,
  opacity?: number,
): void {
  const r = document.createElementNS(NS, "rect");
  r.setAttribute("x", String(x));
  r.setAttribute("y", String(y));
  r.setAttribute("width", String(Math.max(0, w)));
  r.setAttribute("height", String(Math.max(0, h)));
  r.setAttribute("rx", String(rx));
  r.setAttribute("fill", fill);
  if (opacity != null) r.setAttribute("opacity", String(opacity));
  svg.appendChild(r);
}

/** Build a sofa silhouette <svg>. Mirrors sofa-shapes.jsx:3-134 geometry. */
export function sofaSVG(opts: SofaSVGOpts): SVGSVGElement {
  const body = opts.body ?? "#E9E4DA";
  const cushion = opts.cushion ?? body;
  const legColor = opts.legs ?? "#1A1A1A";
  const shadow = opts.shadow ?? "transparent";
  const arm = opts.arm ?? "soft";
  const cushionStyle = opts.cushionStyle ?? "plump";
  const seats = Math.max(1, opts.seats);
  const chaise = !!opts.chaise;

  const cushW = seats === 2 ? 80 : 60;
  const cushGap = 4;
  const armW = arm === "pill" ? 28 : arm === "flat" ? 18 : 26;
  const baseW = seats * cushW + (seats - 1) * cushGap + armW * 2;
  const chaiseW = chaise ? 90 : 0;
  const totalW = baseW + chaiseW;
  const armRadius = arm === "pill" ? 30 : arm === "flat" ? 6 : 18;
  const bodyRadius = cushionStyle === "sleek" ? 8 : cushionStyle === "tight" ? 14 : 22;

  const VBW = 400;
  const VBH = 240;
  const scale = Math.min(VBW / (totalW + 40), 1);
  const sofaW = totalW * scale;
  const sofaH = 130 * scale;
  const x0 = (VBW - sofaW) / 2;
  const y0 = (VBH - sofaH) / 2 - 4;

  const sx = (n: number) => x0 + n * scale;
  const sy = (n: number) => y0 + n * scale;

  const backH = 70;
  const seatH = 40;
  const backY = 0;
  const seatY = backH;

  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", `0 0 ${VBW} ${VBH}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.classList.add("sofa-svg");

  // ground shadow
  if (shadow !== "transparent") {
    const e = document.createElementNS(NS, "ellipse");
    e.setAttribute("cx", String(VBW / 2));
    e.setAttribute("cy", String(y0 + sofaH + 14));
    e.setAttribute("rx", String(sofaW * 0.45));
    e.setAttribute("ry", "5");
    e.setAttribute("fill", shadow);
    svg.appendChild(e);
  }

  // backrest
  rect(svg, sx(armW * 0.6), sy(backY), (totalW - armW * 1.2) * scale, backH * scale, bodyRadius * scale * 0.6, body);
  // seat base
  rect(svg, sx(armW * 0.4), sy(seatY), (totalW - armW * 0.8) * scale, seatH * scale, bodyRadius * scale * 0.5, body);
  // left arm
  rect(svg, sx(0), sy(backY + 12), armW * scale, (seatH + backH - 12) * scale, armRadius * scale, body);
  // right arm (unless chaise)
  if (!chaise) {
    rect(svg, sx(totalW - armW), sy(backY + 12), armW * scale, (seatH + backH - 12) * scale, armRadius * scale, body);
  }
  // cushions
  for (let i = 0; i < seats; i++) {
    const cx = armW + i * (cushW + cushGap);
    const ch = cushionStyle === "plump" ? 50 : cushionStyle === "sleek" ? 30 : 42;
    rect(
      svg,
      sx(cx),
      sy(backY + (backH - ch - 4)),
      cushW * scale,
      ch * scale,
      cushionStyle === "sleek" ? 4 : 14,
      cushion,
      0.96,
    );
  }
  // chaise extension
  if (chaise) {
    rect(svg, sx(baseW - armW * 0.4), sy(seatY - 6), chaiseW * scale, (seatH + 6) * scale, bodyRadius * scale * 0.5, body);
  }
  // legs
  const legCount = seats + (chaise ? 2 : 1);
  const legY = sy(backY + backH + seatH);
  const legH = 14 * scale;
  const legW = 5 * scale;
  const startX = sx(armW * 0.4);
  const endX = sx(totalW - armW * 0.4);
  const span = endX - startX;
  for (let i = 0; i <= legCount; i++) {
    const lx = startX + (span * i) / legCount - legW / 2;
    rect(svg, lx, legY, legW, legH, 1, legColor);
  }

  return svg;
}
